import { useCallback, useEffect, useState } from 'react';

import type { QuickPlanMode } from '../components/quick-plan-mode-selector';
import type { QuickPlanFormValues } from './use-quick-plan-form';

export type QuickPlanConsentStep = 'identity' | 'waiting' | 'health';

interface UseQuickPlanDialogStateOptions {
  open: boolean;
  initialMode?: QuickPlanMode;
}

export function useQuickPlanDialogState({ open, initialMode }: UseQuickPlanDialogStateOptions) {
  const [mode, setMode] = useState<QuickPlanMode | null>(initialMode ?? null);
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  const [consentStep, setConsentStep] = useState<QuickPlanConsentStep>('identity');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [identityValues, setIdentityValues] = useState<Partial<QuickPlanFormValues> | null>(null);
  const [agentEnteredIntakeId, setAgentEnteredIntakeId] = useState<string | null>(null);

  const resetConsentFlow = useCallback(() => {
    setConsentStep('identity');
    setConsentAccepted(false);
    setIdentityValues(null);
    setAgentEnteredIntakeId(null);
  }, []);

  // Submission success closes this controlled dialog from its parent, which does not invoke the
  // dialog primitive's onOpenChange callback. Reset whenever the owning `open` prop closes so a
  // later open always starts a fresh flow instead of restoring the previously selected mode.
  useEffect(() => {
    if (open) return;

    setMode(initialMode ?? null);
    setShowPartnerDialog(false);
    resetConsentFlow();
  }, [initialMode, open, resetConsentFlow]);

  return {
    mode,
    setMode,
    showPartnerDialog,
    setShowPartnerDialog,
    consentStep,
    setConsentStep,
    consentAccepted,
    setConsentAccepted,
    identityValues,
    setIdentityValues,
    agentEnteredIntakeId,
    setAgentEnteredIntakeId,
    resetConsentFlow,
  };
}
